import React from 'react';
import { Button } from './Button';
import { Select } from './Input';
import { AppSettings } from '../types';
import { ThemeMode } from './ThemeProvider';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onSettingsChange: (settings: AppSettings) => void;
  onSave: () => void;
  canSave: boolean;
  isSaving: boolean;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  onDeleteData: () => void;
  onDeleteAccount: () => void;
  onTestSound: () => void;
}

const SOUND_OPTIONS: { value: AppSettings['soundId']; label: string }[] = [
  { value: 'classic', label: 'Classic beep' },
  { value: 'soft', label: 'Soft tone' },
  { value: 'bright', label: 'Bright pulse' },
  { value: 'pulse', label: 'Pulse alarm' },
  { value: 'chime', label: 'Chime' },
];

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
  onSave,
  canSave,
  isSaving,
  theme,
  onThemeChange,
  onDeleteData,
  onDeleteAccount,
  onTestSound,
}) => {
  if (!isOpen) return null;
  const intervalId = 'settings-default-interval';
  const volumeId = 'settings-volume';

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-surface rounded-2xl shadow-2xl p-6 sm:p-8 max-w-lg w-full border border-border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-text">Settings</h3>
          <button onClick={onClose} className="text-muted hover:text-text">
            ✕
          </button>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-text uppercase tracking-wider">Timer</h4>
            <div className="flex justify-between items-center">
              <label htmlFor={intervalId} className="text-sm text-text">Default interval</label>
              <span className="text-sm font-semibold text-primary">{settings.defaultInterval} min</span>
            </div>
            <input
              id={intervalId}
              type="range"
              min="5"
              max="120"
              step="5"
              value={settings.defaultInterval}
              onChange={(event) =>
                onSettingsChange({ ...settings, defaultInterval: Number(event.target.value) })
              }
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-text uppercase tracking-wider">Sound</h4>
            <div className="flex items-center justify-between">
              <label className="text-sm text-text">Enable sound</label>
              <input
                type="checkbox"
                checked={settings.soundEnabled}
                onChange={(event) =>
                  onSettingsChange({ ...settings, soundEnabled: event.target.checked })
                }
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
            </div>
            <Select
              label="Sound option"
              value={settings.soundId}
              disabled={!settings.soundEnabled}
              onChange={(event) =>
                onSettingsChange({ ...settings, soundId: event.target.value as AppSettings['soundId'] })
              }
            >
              {SOUND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <div className="flex items-center gap-2">
              <label htmlFor={volumeId} className="text-xs text-muted">Volume</label>
              <input
                id={volumeId}
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.volume}
                disabled={!settings.soundEnabled}
                onChange={(event) =>
                  onSettingsChange({ ...settings, volume: Number(event.target.value) })
                }
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>
            <Button variant="outline" size="sm" onClick={onTestSound} disabled={!settings.soundEnabled}>
              Test sound
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-text uppercase tracking-wider">Theme</h4>
            <Select label="Theme" value={theme} onChange={(event) => onThemeChange(event.target.value as ThemeMode)}>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </Select>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-text uppercase tracking-wider">Email</h4>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text font-medium">Product updates</p>
                <p className="text-xs text-muted">Receive productivity tips and new feature announcements.</p>
              </div>
              <input
                type="checkbox"
                checked={settings.newsletterOptIn}
                onChange={(event) =>
                  onSettingsChange({ ...settings, newsletterOptIn: event.target.checked })
                }
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-text uppercase tracking-wider">Data</h4>
            <p className="text-sm text-muted">Delete your saved report data whenever you want.</p>
            <Button variant="outline" onClick={onDeleteData}>
              Delete report data
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-text uppercase tracking-wider">Account</h4>
            <p className="text-sm text-muted">Delete your account and receive a confirmation email.</p>
            <Button variant="danger" onClick={onDeleteAccount}>
              Delete account
            </Button>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving...' : 'Save settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};
